/**
 * AiSketchPanel: turns a written community description into a map sketch.
 *
 * Flow: "Generate map sketch" -> POST /v1/cois/sketch -> the candidate
 * polygon previews on the shared community map (via onPreview), a
 * plain-language step log ("Found Lake Merritt · buffered 800 m · clipped
 * to Demo City"), warnings, and refine controls (Expand / Shrink / free-text
 * note, sent back with history for multi-turn revision). "Use this boundary"
 * attaches the polygon to the COI draft.
 *
 * Honesty rules: the panel always labels the sketch a starting point, and
 * shows a "demo mode" badge when the backend reports provider=mock (no LLM
 * key configured) instead of pretending a model drew it.
 */
import { useEffect, useState } from 'react';
import {
  postSketch,
  type CoiSketchProvenance,
  type SketchJurisdiction,
  type SketchResult,
  type SketchTurn,
} from '../../lib/coi';
import { useTenantStore } from '../../stores/tenant';

type SketchStatus = 'idle' | 'loading' | 'ready' | 'error';

interface AiSketchPanelProps {
  /** Current text of the "Describe your community" textarea. */
  narrative: string;
  /**
   * Called when the user accepts the sketch as their draft boundary.
   * The second argument carries the sketch provenance (provider, steps,
   * warnings) so it can travel with the submission.
   */
  onAccept: (polygon: Record<string, unknown>, sketch: CoiSketchProvenance) => void;
  /** True once a boundary has been accepted (shows the attached state). */
  accepted: boolean;
  /**
   * Called with each new sketch candidate (or null when cleared) so the
   * shared community map can preview it before the resident attaches it.
   */
  onPreview: (polygon: Record<string, unknown> | null) => void;
}

export function AiSketchPanel({ narrative, onAccept, accepted, onPreview }: AiSketchPanelProps): JSX.Element {
  const tenant = useTenantStore((s) => s.tenant);
  const [status, setStatus] = useState<SketchStatus>('idle');
  const [result, setResult] = useState<SketchResult | null>(null);
  const [history, setHistory] = useState<SketchTurn[]>([]);
  const [lastDescription, setLastDescription] = useState('');
  const [refineNote, setRefineNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  // The candidate polygon previews on the shared community map.
  useEffect(() => {
    onPreview(result?.polygon_geojson ?? null);
  }, [result, onPreview]);

  const canGenerate = narrative.trim().length >= 10 && status !== 'loading';
  const bounds = tenant?.map.bounds;

  function jurisdiction(): SketchJurisdiction {
    return {
      name: tenant?.name ?? 'this jurisdiction',
      bbox: bounds ?? [-122.36, 37.7, -122.1, 37.86],
    };
  }

  async function runSketch(description: string, turns: SketchTurn[]): Promise<void> {
    setStatus('loading');
    setError(null);
    try {
      const res = await postSketch(description, turns, jurisdiction());
      setResult(res);
      setHistory([...turns, { description, ops: res.ops }]);
      setLastDescription(description);
      setStatus('ready');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'The sketch assistant could not be reached.',
      );
      setStatus('error');
    }
  }

  function handleGenerate(): void {
    void runSketch(narrative.trim(), []);
  }

  function handleRefine(kind: 'expand' | 'shrink' | 'note'): void {
    const note =
      kind === 'expand'
        ? 'Expand the boundary outward a bit'
        : kind === 'shrink'
          ? 'Shrink the boundary inward a bit'
          : refineNote.trim();
    if (!note) return;
    setRefineNote('');
    void runSketch(note, history);
  }

  const showPreview = (status === 'ready' || status === 'loading') && result !== null;

  const showDemoBadge = result?.provider === 'mock';

  return (
    <div className="rounded-lg border border-stone-200 bg-white px-4 py-4" aria-label="AI map sketch">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-stone-800">Map sketch assistant</h3>
          <p className="mt-1 text-xs leading-relaxed text-stone-500">
            Describe your community above, then generate a sketch. The AI sketch is a{' '}
            <span className="font-medium text-stone-700">starting point</span> — review and
            adjust it before submitting.
          </p>
        </div>
        {showDemoBadge && (
          <span
            className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-stone-500"
            title="No AI model is configured on this site yet, so sketches come from a built-in demo script."
          >
            demo mode
          </span>
        )}
      </div>

      {status === 'idle' && (
        <button
          type="button"
          onClick={handleGenerate}
          disabled={!canGenerate}
          className="mt-3 rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong disabled:cursor-not-allowed disabled:opacity-50"
          title={
            narrative.trim().length < 10
              ? 'Describe your community above first (a sentence or two is enough)'
              : 'Generate a map sketch from your description'
          }
        >
          ✨ Generate map sketch
        </button>
      )}

      {status === 'loading' && (
        <div className="mt-3 flex items-center gap-2 text-sm text-stone-600" aria-live="polite">
          <span
            aria-hidden="true"
            className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-stone-300 border-t-tenant-primary"
          />
          Sketching your community…
        </div>
      )}

      {status === 'error' && error && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <p>{error}</p>
          <button
            type="button"
            onClick={handleGenerate}
            className="mt-1 font-medium underline hover:no-underline"
          >
            Try again
          </button>
        </div>
      )}

      {showPreview && result && (
        <div className="mt-3 space-y-3">
          {showPreview && result && (
            <p className="rounded-lg bg-tenant-soft px-3 py-2 text-xs text-tenant-strong">
              The candidate is previewing on the map — pan around to review it, then refine
              below or attach it.
            </p>
          )}
          {!result.polygon_geojson && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              The assistant couldn&apos;t draw an area from that description. Try naming a nearby
              landmark or street.
            </p>
          )}
          {result.steps.length > 0 && (
            <ol className="space-y-1 text-xs text-stone-600" aria-label="How the sketch was built">
              {result.steps.map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span aria-hidden="true" className="text-stone-400">{i + 1}.</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}
          {result.warnings.length > 0 && (
            <ul className="space-y-1" aria-label="Sketch warnings">
              {result.warnings.map((warning, i) => (
                <li
                  key={i}
                  className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-800"
                >
                  ⚠ {warning}
                </li>
              ))}
            </ul>
          )}
          {result.notes.length > 0 && (
            <p className="text-xs italic text-stone-500">{result.notes.join(' ')}</p>
          )}

          {result.polygon_geojson && (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-stone-600">Refine:</span>
                <button
                  type="button"
                  onClick={() => handleRefine('expand')}
                  disabled={status === 'loading'}
                  className="rounded-lg border border-stone-300 bg-white px-2.5 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                >
                  ➕ Expand
                </button>
                <button
                  type="button"
                  onClick={() => handleRefine('shrink')}
                  disabled={status === 'loading'}
                  className="rounded-lg border border-stone-300 bg-white px-2.5 py-1 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                >
                  ➖ Shrink
                </button>
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={refineNote}
                  onChange={(e) => setRefineNote(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleRefine('note');
                    }
                  }}
                  placeholder='Not quite? e.g. "include the park on 14th"'
                  aria-label="Refine the sketch with a note"
                  className="min-w-0 flex-1 rounded-lg border border-stone-300 px-3 py-1.5 text-sm focus:border-tenant-accent focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => handleRefine('note')}
                  disabled={!refineNote.trim() || status === 'loading'}
                  className="shrink-0 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                >
                  Refine
                </button>
              </div>
              <div className="flex items-center gap-3 border-t border-stone-100 pt-3">
                <button
                  type="button"
                  onClick={() =>
                    onAccept(result.polygon_geojson as Record<string, unknown>, {
                      provider: result.provider,
                      steps: result.steps,
                      warnings: result.warnings,
                      notes: result.notes,
                    } satisfies CoiSketchProvenance)
                  }
                  className="rounded-lg bg-tenant-primary px-4 py-2 text-sm font-medium text-white hover:bg-tenant-strong"
                >
                  {accepted ? '✓ Boundary attached — replace it' : 'Use this boundary'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setResult(null);
                    setHistory([]);
                    setStatus('idle');
                  }}
                  className="text-xs font-medium text-stone-500 underline hover:no-underline"
                >
                  Start over
                </button>
              </div>
            </>
          )}
          {lastDescription && (
            <p className="text-[11px] text-stone-400">
              Last sketch: “{lastDescription.length > 80 ? `${lastDescription.slice(0, 80)}…` : lastDescription}”
            </p>
          )}
        </div>
      )}
    </div>
  );
}
